import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Info, UserCheck, UserPlus, Zap, type LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, Notification } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// Per-type icon treatment. Each entry renders its own icon so a notification
// type is visually distinguishable at a glance:
//   system              → neutral info glyph
//   transfer_bonus      → amber bolt (existing)
//   new_verified_signup → emerald "verified user" glyph (admin, PR #882)
const NOTIFICATION_TYPE_ICONS: Record<string, { Icon: LucideIcon; className: string }> = {
    system: { Icon: Info, className: '' },
    transfer_bonus: { Icon: Zap, className: 'text-amber-400' },
    // new_signup: sky "new user" glyph — admin, still unverified (PR #885).
    new_signup: { Icon: UserPlus, className: 'text-sky-400' },
    new_verified_signup: { Icon: UserCheck, className: 'text-emerald-400' },
};

function NotificationTypeIcon({ type }: { type: string }) {
    const entry = NOTIFICATION_TYPE_ICONS[type];
    if (!entry) return null;

    const { Icon, className } = entry;
    return (
        <Icon
            data-testid={`notification-icon-${type}`}
            aria-hidden="true"
            className={`w-3.5 h-3.5 ${className}`.trim()}
        />
    );
}

type NotificationFeedProps = {
    isOpen: boolean;
    membershipTier?: string | null;
    onClose?: () => void;
    maxItems?: number;
    className?: string;
};

function relativeTime(dateStr: string): string {
    try {
        return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
        return '';
    }
}

function canShowNotification(notification: Notification, membershipTier?: string | null) {
    if (notification.type === 'transfer_bonus') {
        return membershipTier?.toUpperCase() === 'APOLLO';
    }

    return true;
}

export default function NotificationFeed({
    isOpen,
    membershipTier,
    onClose,
    maxItems = 10,
    className = '',
}: NotificationFeedProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: unreadData } = useQuery({
        queryKey: queryKeys.notificationUnreadCount,
        queryFn: () => api.getUnreadCount(),
        enabled: isOpen,
        refetchInterval: 60_000,
    });

    const { data: notifications = [], isLoading } = useQuery({
        queryKey: queryKeys.notifications,
        queryFn: () => api.getNotifications(),
        enabled: isOpen,
        staleTime: 30_000,
    });

    const unreadCount = unreadData?.count ?? 0;
    const filteredNotifications = notifications.filter((notification) =>
        canShowNotification(notification, membershipTier),
    );

    async function handleMarkRead(notification: Notification) {
        if (!notification.isRead) {
            await api.markNotificationRead(notification.id);
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
            queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount });
        }

        onClose?.();
        if (notification.actionUrl) {
            navigate(notification.actionUrl);
        }
    }

    async function handleMarkAllRead() {
        await api.markAllNotificationsRead();
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
        queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount });
    }

    return (
        <div className={className}>
            <div className="flex items-center justify-between px-3 py-2">
                <div className="p-0 text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Notifications</div>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={handleMarkAllRead}
                        className="text-[9px] font-bold text-pantheon-teal hover:text-pantheon-teal/80 tracking-wide uppercase transition-colors"
                    >
                        Mark all read
                    </button>
                )}
            </div>
            <div className="bg-white/5 mx-1 h-px" />
            <div className="max-h-80 overflow-y-auto">
                {isLoading ? (
                    [0, 1, 2].map((i) => (
                        <div key={i} className="flex flex-col gap-1 p-3 mb-1">
                            <div className="h-3 bg-white/10 rounded animate-pulse w-3/4" />
                            <div className="h-2 bg-white/5 rounded animate-pulse w-1/2 mt-1" />
                        </div>
                    ))
                ) : filteredNotifications.length === 0 ? (
                    <div className="px-3 py-6 text-center">
                        <p className="text-[10px] text-white/40">No notifications yet</p>
                    </div>
                ) : (
                    filteredNotifications.slice(0, maxItems).map((notification) => (
                        <button
                            key={notification.id}
                            type="button"
                            role="menuitem"
                            onClick={() => handleMarkRead(notification)}
                            className="mb-1 flex w-full cursor-pointer items-start gap-2.5 rounded-lg border-none !bg-[rgba(255,255,255,0.04)] p-3 text-left !text-white transition-colors hover:!bg-[rgba(255,255,255,0.08)] focus:!bg-[rgba(255,255,255,0.08)] focus:!text-white focus-visible:!bg-[rgba(255,255,255,0.08)] focus-visible:!text-white active:!bg-[rgba(44,192,207,0.14)] data-[highlighted]:!bg-[rgba(255,255,255,0.08)] data-[highlighted]:!text-white"
                        >
                            <div className="mt-0.5 flex-shrink-0 text-white/40">
                                <NotificationTypeIcon type={notification.type} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    {!notification.isRead && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-pantheon-teal flex-shrink-0" />
                                    )}
                                    <span className={`text-[11px] font-bold leading-tight truncate ${notification.isRead ? 'text-white/60' : 'text-white'}`}>
                                        {notification.title}
                                    </span>
                                </div>
                                <p className="text-[10px] text-white/40 mt-0.5 line-clamp-2 leading-relaxed">{notification.body}</p>
                                <span className="text-[9px] text-white/25 mt-1 block">{relativeTime(notification.createdAt)}</span>
                            </div>
                        </button>
                    ))
                )}
            </div>
        </div>
    );
}
